import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlayCircle, Clock, Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { linkifyContentRefs, parseContentHref, formatSeconds, contentHref } from '@/lib/liaContentLinks';

function LiaContentCard({ id, seconds, label, onNavigate }: { id: string; seconds: number | null; label: string; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['lia-content-card', id],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from('content_bank')
        .select('id, name, thumbnail_url, duration, access_level, planning_status')
        .eq('id', id)
        .maybeSingle();
      return data;
    },
  });
  if (data && data.planning_status === 'archived') return <span>{label}</span>;
  const title = data?.name || label;
  const href = contentHref(id, seconds);
  return (
    <a
      href={href}
      onClick={(e) => { e.preventDefault(); onNavigate?.(); navigate(href); }}
      className="not-prose my-2 flex w-full max-w-md items-center gap-3 rounded-xl border border-border bg-card p-2 text-left no-underline transition-colors hover:border-primary hover:bg-accent"
    >
      <span className="relative block h-16 w-28 shrink-0 overflow-hidden rounded-lg bg-muted">
        {data?.thumbnail_url ? (
          <img src={data.thumbnail_url} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : null}
        <span className="absolute inset-0 flex items-center justify-center bg-foreground/20">
          <PlayCircle className="h-7 w-7 text-primary-foreground drop-shadow" />
        </span>
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="line-clamp-2 text-sm font-semibold text-foreground">{title}</span>
        <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {seconds ? <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">min {formatSeconds(seconds)}</span> : null}
          {data?.duration ? <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{data.duration}</span> : null}
          {data?.access_level === 'subscription' ? <span className="flex items-center gap-1"><Lock className="h-3 w-3" />Plan Pro</span> : null}
        </span>
      </span>
    </a>
  );
}

export function LiaMarkdown({ text, onNavigate }: { text: string; onNavigate?: () => void }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children }) => {
          const ref = href ? parseContentHref(href) : null;
          if (ref) {
            const label = Array.isArray(children) ? children.join('') : String(children ?? '');
            return <LiaContentCard id={ref.id} seconds={ref.seconds} label={label} onNavigate={onNavigate} />;
          }
          return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
        },
      }}
    >
      {linkifyContentRefs(text)}
    </ReactMarkdown>
  );
}
